import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Calendar, Plus, Clock, MapPin, User, 
  Loader2, ChevronRight, Users, Check, X, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Events = () => {
  const { user, isOrganizer, canManagePrograms } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [newProgram, setNewProgram] = useState({
    title: '',
    description: '',
    program_date: '',
    venue: '',
    activities: []
  });
  const [newActivity, setNewActivity] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    speaker: ''
  });

  // Can manage = organizer OR has Program committee permission
  const canManage = (isOrganizer && isOrganizer()) || (canManagePrograms && canManagePrograms());

  useEffect(() => {
    if (isOrganizer && isOrganizer()) {
      fetchEvents();
    } else {
      if (user?.event_id) {
        setSelectedEventId(user.event_id);
      }
    }
  }, [user, isOrganizer]);

  useEffect(() => {
    if (selectedEventId) {
      fetchPrograms();
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`);
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load events');
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/event/programs?event_id=${selectedEventId}`);
      setPrograms(response.data);
    } catch (error) {
      toast.error('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProgram = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/event/programs?event_id=${selectedEventId}`, newProgram);
      toast.success('Program created successfully');
      setNewProgram({
        title: '',
        description: '',
        program_date: '',
        venue: '',
        activities: []
      });
      setCreateDialogOpen(false);
      fetchPrograms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create program');
    }
  };

  const addActivityToProgram = () => {
    if (!newActivity.title || !newActivity.start_time || !newActivity.end_time) {
      toast.error('Please fill in activity title and times');
      return;
    }
    setNewProgram({
      ...newProgram,
      activities: [...newProgram.activities, { ...newActivity }]
    });
    setNewActivity({
      title: '',
      description: '',
      start_time: '',
      end_time: '',
      location: '',
      speaker: ''
    });
  };

  const removeActivityFromProgram = (index) => {
    setNewProgram({
      ...newProgram,
      activities: newProgram.activities.filter((_, i) => i !== index)
    });
  };

  const handleRSVP = async (programId, activityId) => {
    try {
      await axios.post(`${API_URL}/api/event/programs/${programId}/rsvp/${activityId}`);
      toast.success('RSVP confirmed!');
      fetchPrograms();
      if (selectedProgram) {
        const updatedProgram = programs.find(p => p.id === programId);
        if (updatedProgram) {
          const response = await axios.get(`${API_URL}/api/event/programs?event_id=${selectedEventId}`);
          const updated = response.data.find(p => p.id === programId);
          setSelectedProgram(updated);
        }
      }
    } catch (error) {
      toast.error('Failed to RSVP');
    }
  };

  const handleCancelRSVP = async (programId, activityId) => {
    try {
      await axios.delete(`${API_URL}/api/event/programs/${programId}/rsvp/${activityId}`);
      toast.success('RSVP cancelled');
      fetchPrograms();
      if (selectedProgram) {
        const response = await axios.get(`${API_URL}/api/event/programs?event_id=${selectedEventId}`);
        const updated = response.data.find(p => p.id === programId);
        setSelectedProgram(updated);
      }
    } catch (error) {
      toast.error('Failed to cancel RSVP');
    }
  };

  const handleDeleteProgram = async (programId) => {
    if (!window.confirm('Delete this program?')) return;
    try {
      await axios.delete(`${API_URL}/api/event/programs/${programId}`);
      toast.success('Program deleted');
      setSelectedProgram(null);
      fetchPrograms();
    } catch (error) {
      toast.error('Failed to delete program');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-PH', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-maroon" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="events-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <Calendar className="h-8 w-8" />
              Sequence of Events
            </h1>
            <p className="text-gray-600 mt-1">
              View and RSVP to reunion programs
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {canManage && events.length > 1 && (
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.event_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {canManage && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full" data-testid="create-program-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Program
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-maroon">Create New Program</DialogTitle>
                    <DialogDescription>Plan the sequence of events for your reunion</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProgram} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Program Title</Label>
                        <Input
                          value={newProgram.title}
                          onChange={(e) => setNewProgram({ ...newProgram, title: e.target.value })}
                          placeholder="Grand Reunion 2025"
                          required
                          data-testid="program-title-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Program Date</Label>
                        <Input
                          type="date"
                          value={newProgram.program_date}
                          onChange={(e) => setNewProgram({ ...newProgram, program_date: e.target.value })}
                          required
                          data-testid="program-date-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Venue</Label>
                      <Input
                        value={newProgram.venue}
                        onChange={(e) => setNewProgram({ ...newProgram, venue: e.target.value })}
                        placeholder="School Gymnasium, Main Campus"
                        required
                        data-testid="program-venue-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea
                        value={newProgram.description}
                        onChange={(e) => setNewProgram({ ...newProgram, description: e.target.value })}
                        placeholder="Program details..."
                        data-testid="program-description-input"
                      />
                    </div>

                    {/* Activities Section */}
                    <div className="border-t pt-6">
                      <h3 className="font-heading text-lg font-semibold text-maroon mb-4">Program Activities</h3>
                      
                      {/* Added Activities */}
                      {newProgram.activities.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {newProgram.activities.map((activity, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-cream-paper rounded-lg">
                              <div>
                                <p className="font-medium">{activity.title}</p>
                                <p className="text-sm text-gray-500">{activity.start_time} - {activity.end_time}</p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeActivityFromProgram(index)}
                                className="text-red-500"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Activity Form */}
                      <div className="p-4 border border-dashed border-gray-300 rounded-xl space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            value={newActivity.title}
                            onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                            placeholder="Activity title"
                            data-testid="activity-title-input"
                          />
                          <Input
                            value={newActivity.speaker}
                            onChange={(e) => setNewActivity({ ...newActivity, speaker: e.target.value })}
                            placeholder="Speaker/Host (optional)"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            type="time"
                            value={newActivity.start_time}
                            onChange={(e) => setNewActivity({ ...newActivity, start_time: e.target.value })}
                            data-testid="activity-start-time"
                          />
                          <Input
                            type="time"
                            value={newActivity.end_time}
                            onChange={(e) => setNewActivity({ ...newActivity, end_time: e.target.value })}
                            data-testid="activity-end-time"
                          />
                        </div>
                        <Input
                          value={newActivity.location}
                          onChange={(e) => setNewActivity({ ...newActivity, location: e.target.value })}
                          placeholder="Location (optional)"
                        />
                        <Textarea
                          value={newActivity.description}
                          onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                          placeholder="Activity description (optional)"
                          rows={2}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={addActivityToProgram}
                          className="w-full border-maroon text-maroon hover:bg-maroon/5"
                          data-testid="add-activity-btn"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Activity
                        </Button>
                      </div>
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-maroon hover:bg-maroon-hover text-white" data-testid="submit-program-btn">
                        Create Program
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Programs List */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Programs Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {programs.length === 0 ? (
              <Card className="border-0 shadow-sm bg-white">
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">No programs scheduled yet</p>
                </CardContent>
              </Card>
            ) : (
              programs.map((program) => (
                <Card 
                  key={program.id}
                  className={`border-0 shadow-sm cursor-pointer transition-all duration-300 ${
                    selectedProgram?.id === program.id 
                      ? 'bg-maroon text-white shadow-lg' 
                      : 'bg-white hover:shadow-md'
                  }`}
                  onClick={() => setSelectedProgram(program)}
                  data-testid={`program-card-${program.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-heading text-lg font-semibold">{program.title}</h3>
                        <div className={`flex items-center gap-2 mt-2 text-sm ${
                          selectedProgram?.id === program.id ? 'text-white/80' : 'text-gray-500'
                        }`}>
                          <Calendar className="h-4 w-4" />
                          {formatDate(program.program_date)}
                        </div>
                        <div className={`flex items-center gap-2 mt-1 text-sm ${
                          selectedProgram?.id === program.id ? 'text-white/80' : 'text-gray-500'
                        }`}>
                          <MapPin className="h-4 w-4" />
                          {program.venue}
                        </div>
                      </div>
                      <ChevronRight className={`h-5 w-5 ${
                        selectedProgram?.id === program.id ? 'text-white' : 'text-gray-400'
                      }`} />
                    </div>
                    <Badge className={`mt-3 ${
                      selectedProgram?.id === program.id 
                        ? 'bg-white/20 text-white' 
                        : 'bg-gold/20 text-gold-foreground'
                    }`}>
                      {program.activities?.length || 0} activities
                    </Badge>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Program Details / Timeline */}
          <div className="lg:col-span-2">
            {selectedProgram ? (
              <Card className="border-0 shadow-sm bg-white" data-testid="program-details">
                <CardHeader className="border-b border-gray-100">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-heading text-2xl text-maroon">{selectedProgram.title}</CardTitle>
                      <CardDescription className="mt-2">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(selectedProgram.program_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {selectedProgram.venue}
                          </span>
                        </div>
                      </CardDescription>
                      {selectedProgram.description && (
                        <p className="mt-3 text-gray-600">{selectedProgram.description}</p>
                      )}
                    </div>
                    {canManage && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteProgram(selectedProgram.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid="delete-program-btn"
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-maroon mb-6">Program Timeline</h3>
                  
                  {selectedProgram.activities?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p>No activities scheduled yet</p>
                    </div>
                  ) : (
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gold" />
                      
                      {/* Activities */}
                      <div className="space-y-6">
                        {selectedProgram.activities?.map((activity, index) => {
                          const isRSVPd = activity.rsvp_users?.includes(user?.id);
                          const rsvpCount = activity.rsvp_users?.length || 0;
                          
                          return (
                            <div key={activity.id || index} className="relative pl-16" data-testid={`activity-${activity.id || index}`}>
                              {/* Timeline dot */}
                              <div className="absolute left-4 top-2 w-5 h-5 rounded-full bg-gold border-4 border-cream" />
                              
                              <div className={`p-5 rounded-xl border transition-all ${
                                isRSVPd 
                                  ? 'bg-green-50 border-green-200' 
                                  : 'bg-white border-gray-200 hover:border-gold'
                              }`}>
                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 text-sm text-maroon font-medium mb-2">
                                      <Clock className="h-4 w-4" />
                                      {activity.start_time} - {activity.end_time}
                                    </div>
                                    <h4 className="font-heading text-lg font-semibold text-gray-900">
                                      {activity.title}
                                    </h4>
                                    {activity.description && (
                                      <p className="text-gray-600 mt-1 text-sm">{activity.description}</p>
                                    )}
                                    <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                                      {activity.speaker && (
                                        <span className="flex items-center gap-1">
                                          <User className="h-4 w-4" />
                                          {activity.speaker}
                                        </span>
                                      )}
                                      {activity.location && (
                                        <span className="flex items-center gap-1">
                                          <MapPin className="h-4 w-4" />
                                          {activity.location}
                                        </span>
                                      )}
                                      <span className="flex items-center gap-1">
                                        <Users className="h-4 w-4" />
                                        {rsvpCount} attending
                                      </span>
                                    </div>
                                  </div>
                                  <div className="shrink-0">
                                    {isRSVPd ? (
                                      <Button
                                        variant="outline"
                                        onClick={() => handleCancelRSVP(selectedProgram.id, activity.id)}
                                        className="border-green-500 text-green-600 hover:bg-green-50"
                                        data-testid={`cancel-rsvp-${activity.id}`}
                                      >
                                        <Check className="h-4 w-4 mr-2" />
                                        Going
                                      </Button>
                                    ) : (
                                      <Button
                                        onClick={() => handleRSVP(selectedProgram.id, activity.id)}
                                        className="bg-maroon hover:bg-maroon-hover text-white"
                                        data-testid={`rsvp-btn-${activity.id}`}
                                      >
                                        RSVP
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-sm bg-white h-full min-h-[400px] flex items-center justify-center">
                <CardContent className="text-center">
                  <Calendar className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-600">Select a program</h3>
                  <p className="text-gray-400 mt-1">Click on a program to view its timeline</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
  );
};

export default Events;
